/* Angular  */
import { APP_INITIALIZER, ErrorHandler, Injector, NgModule, DestroyRef } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';

import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { MaterialModule } from './modules/material/material.module';
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

/* localization */
import { DateAdapter } from '@angular/material/core';

import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';

/* dayjs */
import 'dayjs/locale/cs';
import 'dayjs/locale/de';
import 'dayjs/locale/en';
import 'dayjs/locale/hr';
import 'dayjs/locale/hu';
import 'dayjs/locale/pl';
import 'dayjs/locale/ro';
import 'dayjs/locale/ru';
import 'dayjs/locale/sk';
import 'dayjs/locale/uk';

import localeCs from '@angular/common/locales/cs';
import localeDe from '@angular/common/locales/de';
import localeEn from '@angular/common/locales/en-GB';
import localeHr from '@angular/common/locales/hr';
import localeHu from '@angular/common/locales/hu';
import localePl from '@angular/common/locales/pl';
import localeRo from '@angular/common/locales/ro';
import localeRu from '@angular/common/locales/ru';
import localeSk from '@angular/common/locales/sk';
import localeUk from '@angular/common/locales/uk';

import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

import { LOCATION_INITIALIZED, registerLocaleData } from '@angular/common';

/* Pages */
import { LandingPageComponent } from './pages/landing-page/landing-page.component';
import { RefundPageComponent } from './pages/refund-page/refund-page.component';
import { PaymentCallbackComponent } from './pages/payment-callback/payment-callback.component';

import { persistState } from '@datorama/akita';

/* Hostware*/
import { HwAuthInterceptor } from './interceptors/hw-auth-interceptor';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { CustomDatePickerAdapter } from './adapters/date-adapter';
import { BasketComponent } from './components/basket/basket.component';
import { CloseSvgComponent } from './components/close-svg/close-svg.component';
import { CustomCalendarHeaderComponent } from './components/custom-calendar-header/custom-calendar-header.component';
import { ErrorDialogComponent } from './components/dialog/error-dialog/error-dialog.component';
import { LanguageSelectorDialogComponent } from './components/dialog/language-selector-dialog/language-selector-dialog.component';
import { MessageDialogComponent } from './components/dialog/message-dialog/message-dialog.component';
import { YesNoDialogComponent } from './components/dialog/yes-no-dialog/yes-no-dialog.component';
import { SpinnerComponent } from './components/spinner/spinner.component';
import { DisableCopyDirective, DisableCutDirective, DisablePasteDirective } from './directives/disable-cut-copy-paste.directive';
import { TitleCaseDirective } from './directives/title-case.directive';
import { UrlParam } from './models/url-param';
import { HufPipe } from './pipes/huf.pipe';
import { SafeHtmlPipe } from './pipes/safehtml.pipe';
import { SessionStorageService } from './services/session-storage.service';

const storage = persistState();
dayjs.extend(customParseFormat);

registerLocaleData(localeHu, 'hu');
registerLocaleData(localeEn, 'en');
registerLocaleData(localeDe, 'de');
registerLocaleData(localeUk, 'uk');
registerLocaleData(localeCs, 'cs');
registerLocaleData(localeHr, 'hr');
registerLocaleData(localePl, 'pl');
registerLocaleData(localeRo, 'ro');
registerLocaleData(localeRu, 'ru');
registerLocaleData(localeSk, 'sk');
registerLocaleData(localeEn, 'en');

export const DATE_FORMAT_EN = {
    parse: {
        dateInput: 'LL',
    },
    display: {
        dateInput: 'LL',
        monthYearLabel: 'MMM YYYY',
        dateA11yLabel: 'LL',
        monthYearA11yLabel: 'MMMM YYYY',
    }
};
export const DATE_FORMAT_DE = {
    parse: {
        dateInput: 'dd.MM.yyyy',
    },
    display: {
        dateInput: 'LL',
        monthYearLabel: 'MMM YYYY',
        dateA11yLabel: 'LL',
        monthYearA11yLabel: 'MMMM YYYY',
    }
};
export const DATE_FORMAT_HU = {
    parse: {
        dateInput: 'YYYY.MM.DD',
    },
    display: {
        dateInput: 'YYYY.MM.DD',
        monthYearLabel: 'YYYY.MM',
        dateA11yLabel: 'LL',
        monthYearA11yLabel: 'MMMM YYYY',
    }
};
export const DATE_FORMAT_UK = {
    parse: {
        dateInput: 'dd.MM.yyyy',
    },
    display: {
        dateInput: 'dd.MM.yyyy',
        monthYearLabel: 'MM.YYYY',
        dateA11yLabel: 'LL',
        monthYearA11yLabel: 'MMMM YYYY',
    }
};
export const DATE_FORMAT_CE = {
    parse: {
        dateInput: 'dd.MM.yyyy',
    },
    display: {
        dateInput: 'dd.MM.yyyy',
        monthYearLabel: 'MM.YYYY',
        dateA11yLabel: 'LL',
        monthYearA11yLabel: 'MMMM YYYY',
    }
};
export const DATE_FORMAT_PL = {
    parse: {
        dateInput: 'dd.MM.yyyy',
    },
    display: {
        dateInput: 'dd.MM.yyyy',
        monthYearLabel: 'MM.YYYY',
        dateA11yLabel: 'LL',
        monthYearA11yLabel: 'MMMM YYYY',
    }
};
export const DATE_FORMAT_HR = {
    parse: {
        dateInput: 'dd.MM.yyyy',
    },
    display: {
        dateInput: 'LL',
        monthYearLabel: 'MMM YYYY',
        dateA11yLabel: 'LL',
        monthYearA11yLabel: 'MMMM YYYY',
    }
}
export const DATE_FORMAT_RO = {
    parse: {
        dateInput: 'dd.MM.yyyy',
    },
    display: {
        dateInput: 'LL',
        monthYearLabel: 'MMM YYYY',
        dateA11yLabel: 'LL',
        monthYearA11yLabel: 'MMMM YYYY',
    }
}
export const DATE_FORMAT_RU = {
    parse: {
        dateInput: 'dd.MM.yyyy',
    },
    display: {
        dateInput: 'LL',
        monthYearLabel: 'MMM YYYY',
        dateA11yLabel: 'LL',
        monthYearA11yLabel: 'MMMM YYYY',
    }
}

@NgModule({
    declarations: [
        AppComponent,
        LandingPageComponent,
        BasketComponent,
        SpinnerComponent,
        ErrorDialogComponent,
        YesNoDialogComponent,
        LanguageSelectorDialogComponent,
        MessageDialogComponent,
        HufPipe,
        SafeHtmlPipe,
        PaymentCallbackComponent,
        CustomCalendarHeaderComponent,
        CloseSvgComponent,
        RefundPageComponent,
        TitleCaseDirective,
        DisableCutDirective,
        DisablePasteDirective,
        DisableCopyDirective
    ],
    imports: [
        BrowserModule,
        HttpClientModule,
        AppRoutingModule,
        MaterialModule,
        FormsModule,
        ReactiveFormsModule,
        TranslateModule.forRoot()
    ],
    exports: [
        HufPipe
    ],
    providers: [
        { provide: 'persistStorage', useValue: storage },
        { provide: HTTP_INTERCEPTORS, useClass: HwAuthInterceptor, multi: true },
        {
            provide: APP_INITIALIZER,
            useFactory: lngInitializerFactory,
            deps: [TranslateService, Injector, SessionStorageService, ActivatedRoute],
            multi: true
        },
        { provide: DateAdapter, useClass: CustomDatePickerAdapter },
        ...provideTranslateHttpLoader()
    ],
    bootstrap: [AppComponent]
})
export class AppModule { }

export function lngInitializerFactory(translate: TranslateService, injector: Injector, sessionStorage: SessionStorageService, route: ActivatedRoute,) {
    return () => new Promise<any>((resolve: any) => {

        const locationInitialized = injector.get(LOCATION_INITIALIZED, Promise.resolve(null));

        locationInitialized.then(() => {
            let lang = translate.getBrowserLang() === 'hu' ? 'hu' : 'en'

            const urlParamsFromSessionSorage: UrlParam = sessionStorage.getUrlParams();
            if (urlParamsFromSessionSorage) {
                lang = urlParamsFromSessionSorage.Language;
            }

            translate.setDefaultLang(lang);

            const destroyRef = injector.get(DestroyRef);

            translate.use(lang)
                .pipe(takeUntilDestroyed(destroyRef))
                .subscribe({
                    next: () => {
                        console.info(`Successfully initialized '${lang}' language.'`);
                    },
                    error: () => {
                        console.error(`Problem with '${lang}' language initialization.'`);
                    },
                    complete() {
                        resolve(null);
                    },
                })
        });
    });
}
